import { useState, useEffect, useCallback } from "react";
import { API, AUTH, PASS_TYPES, PASS_LABELS, useClasses } from "../adminUtils";
import "../admin-base.css";
import "../admin-layout.css";
import "../admin-controls.css";
import "../admin-responsive.css";
import "./studentsPassesTab.css";

export default function StudentsPassesTab() {
  const [students, setStudents]               = useState([]);
  const [search, setSearch]                   = useState("");
  const [loading, setLoading]                 = useState(true);
  const [selected, setSelected]               = useState(null);
  const [detailLoading, setDetailLoading]     = useState(false);
  const [showPassForm, setShowPassForm]       = useState(false);
  const [form, setForm]                       = useState({ type: "pass4", classesTotal: 4, expiresAt: "", notes: "" });
  const [saving, setSaving]                   = useState(false);
  const [formError, setFormError]             = useState(null);
  const [formSuccess, setFormSuccess]         = useState(false);
  const [waiver, setWaiver]                   = useState(null);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [waiverRecipient, setWaiverRecipient] = useState("");
  const [waiverSending, setWaiverSending]     = useState(false);
      useEffect(() => {
      const defaultsByType = { pass4: 4, pass8: 8, dropin: 1 };
      setForm(f => ({ ...f, classesTotal: defaultsByType[f.type] ?? f.classesTotal }));
      }, [form.type]);

  const CLASSES = useClasses();

  useEffect(() => {
    fetch(`${API}/students`, { headers: AUTH })
      .then(r => r.json()).then(d => setStudents(d.students ?? []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const loadStudent = useCallback(async email => {
    setDetailLoading(true); setShowPassForm(false); setFormError(null); setFormSuccess(false);
    setWaiver(null);
    try {
      const res  = await fetch(`${API}/students/${email}`, { headers: AUTH });
      const data = await res.json();
      setSelected(data);
      // Fetch waiver status
      const wRes = await fetch(`${API}/waiver/status?email=${encodeURIComponent(email)}`, { headers: AUTH });
      const wData = await wRes.json();
      setWaiver(wData);
    } catch { setSelected(null); }
    finally { setDetailLoading(false); }
  }, []);

  const refreshSelected = () => {
    if (selected?.student?.email) loadStudent(selected.student.email);
  };

  const toggleActive = async (id, active) => {
    if (active && !window.confirm("Deactivate this pass?")) return;
    await fetch(`${API}/passes/${id}`, { method: "PATCH", headers: AUTH, body: JSON.stringify({ active: !active }) });
    refreshSelected();
  };

  const handleCreate = async () => {
    if (!selected?.student?.email) return;
    setSaving(true); setFormError(null); setFormSuccess(false);
    try {
      // const classesByType = { pass4: 4, pass8: 8 };
      const isMembership  = ["member2x","memberUnl"].includes(form.type);
      const body = {
        type: form.type,
        studentEmail: selected.student.email,
        classesTotal: isMembership ? null : Number(form.classesTotal),
        expiresAt: form.expiresAt || null,
        notes: form.notes || "",
      };
      const res = await fetch(`${API}/passes`, { method: "POST", headers: AUTH, body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json(); setFormError(d.error ?? "Failed."); return; }
      setFormSuccess(true);
      setForm({ type: "pass4", classesTotal: 4, expiresAt: "", notes: "" });
      setShowPassForm(false);
      refreshSelected();
    } catch { setFormError("Something went wrong."); }
    finally   { setSaving(false); }
  };

  const filtered = students.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.email?.toLowerCase().includes(search.toLowerCase())
  );

  const isMembership = ["member2x","memberUnl"].includes(form.type);

  return (
    <div className="adm-tab-content adm-two-col">
      {/* ── Left: student list ── */}
      <div className="adm-col-list">
        <input className="adm-input adm-search" placeholder="Search name or email…"
          value={search} onChange={e => setSearch(e.target.value)} />
        {loading && <p className="adm-loading">Loading…</p>}
        <div className="adm-student-list">
          {filtered.map(s => (
            <button key={s._id}
              className={`adm-student-row ${selected?.student?._id === s._id ? "active" : ""}`}
              onClick={() => loadStudent(s.email)}>
              <span className="adm-student-name">{s.name}</span>
              <span className="adm-student-email">{s.email}</span>
            </button>
          ))}
          {!loading && filtered.length === 0 && <p className="adm-empty">No students found.</p>}
        </div>
      </div>

      {/* ── Right: detail panel ── */}
      <div className="adm-col-detail">
        {!selected && !detailLoading && (
          <div className="adm-detail-placeholder"><span>Select a student to view details</span></div>
        )}
        {detailLoading && <p className="adm-loading">Loading…</p>}
        {selected && !detailLoading && (
          <>
            {/* Student header */}
            <div className="adm-detail-header">
              <h2 className="adm-detail-name">{selected.student.name}</h2>
              <p className="adm-detail-email">{selected.student.email}</p>
              {selected.student.notes && <p className="adm-detail-notes">{selected.student.notes}</p>}
            </div>
          {/* Waiver */}
            <div className="sp-waiver-section">
              <h3 className="adm-detail-section">Liability Waiver</h3>
              {waiver?.signed ? (
                <div className="sp-waiver-signed">
                  <span className="sp-waiver-badge sp-waiver-badge--ok">✓ Signed</span>
                  <span className="sp-waiver-date">
                    {new Date(waiver.signedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <button className="adm-btn-ghost-sm" onClick={() => window.open(waiver.pdfUrl, '_blank')}>Print</button>
                  <button className="adm-btn-ghost-sm" onClick={() => {
                    setWaiverRecipient(selected.student.email);
                    setShowEmailPrompt(true);
                  }}>Email</button>
                  {showEmailPrompt && (
                    <div className="sp-waiver-email-prompt">
                      <input
                        className="adm-input"
                        value={waiverRecipient}
                        onChange={e => setWaiverRecipient(e.target.value)}
                        placeholder="Recipient email"
                      />
                      <button className="adm-btn-ghost-sm" disabled={waiverSending} onClick={async () => {
                        setWaiverSending(true);
                        await fetch(`${API}/waiver/send-pdf`, {
                          method: "POST", headers: AUTH,
                          body: JSON.stringify({ email: selected.student.email, recipientEmail: waiverRecipient })
                        });
                        setWaiverSending(false);
                        setShowEmailPrompt(false);
                      }}>{waiverSending ? "Sending…" : "Send"}</button>
                      <button className="adm-btn-ghost-sm" onClick={() => setShowEmailPrompt(false)}>Cancel</button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="sp-waiver-unsigned">
                  <span className="sp-waiver-badge sp-waiver-badge--missing">✗ Not signed</span>
                </div>
              )}
            </div>
            {/* Passes */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"0.6rem" }}>
              <h3 className="adm-detail-section" style={{ margin:0 }}>Passes</h3>
              <button className="adm-btn-ghost-sm" onClick={() => { setShowPassForm(v => !v); setFormError(null); setFormSuccess(false); }}>
                {showPassForm ? "Cancel" : "+ New Pass"}
              </button>
            </div>

            {formSuccess && <p className="adm-saved" style={{ marginBottom:"0.75rem" }}>✓ Pass created</p>}

            {showPassForm && (
              <div className="adm-create-form" style={{ marginBottom:"1rem" }}>
                <div className="adm-form-grid">
                  <label className="adm-form-label">Pass Type
                    <select className="adm-input" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                      {PASS_TYPES.map(t => <option key={t} value={t}>{PASS_LABELS[t]}</option>)}
                    </select>
                  </label>
                  {!isMembership && (
                    <label className="adm-form-label">Classes Total
                      <input className="adm-input" type="number" min="1"
                        value={form.classesTotal} onChange={e => setForm(f => ({ ...f, classesTotal: e.target.value }))} />
                    </label>
                  )}
                  <label className="adm-form-label">Expires (optional)
                    <input className="adm-input" type="date" value={form.expiresAt}
                      onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))} />
                  </label>
                  <label className="adm-form-label" style={{ gridColumn:"1/-1" }}>Notes
                    <input className="adm-input" value={form.notes} placeholder="e.g. Paid in person"
                      onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                  </label>
                </div>
                {formError && <p className="adm-error">{formError}</p>}
                <div className="adm-form-actions">
                  <button className="adm-btn-primary" disabled={saving} onClick={handleCreate}>
                    {saving ? "Creating…" : "Create Pass"}
                  </button>
                </div>
              </div>
            )}

            {selected.passes?.length === 0 && !showPassForm && <p className="adm-empty">No passes.</p>}
            {selected.passes?.map(p => (
              <div key={p._id} className={`adm-pass-card ${!p.active ? "pass-inactive" : ""}`}>
                <div className="adm-pass-top">
                  <span className="adm-pass-code">{p.code}</span>
                  <div style={{ display:"flex", gap:"0.5rem", alignItems:"center" }}>
                    <span className={`adm-pass-status ${p.active ? "pass-active" : "pass-used"}`}>
                      {p.active ? "Active" : "Inactive"}
                    </span>
                    <button
                      className={p.active ? "adm-btn-danger-sm" : "adm-btn-ghost-sm"}
                      onClick={() => toggleActive(p._id, p.active)}>
                      {p.active ? "Deactivate" : "Reactivate"}
                    </button>
                  </div>
                </div>
                <div className="adm-pass-meta">
                  <span>{PASS_LABELS[p.type] ?? p.type}</span>
                  {p.classesTotal != null && <span>{p.classesTotal - p.classesUsed} of {p.classesTotal} remaining</span>}
                  {p.expiresAt && <span>Expires {new Date(p.expiresAt).toLocaleDateString()}</span>}
                </div>
              </div>
            ))}

            {/* Recent Bookings */}
            <h3 className="adm-detail-section">Recent Bookings</h3>
            {selected.bookings?.length === 0 && <p className="adm-empty">No bookings.</p>}
            {selected.bookings?.slice(0, 10).map(b => (
              <div key={b._id} className={`adm-booking-row ${b.status === "cancelled" ? "booking-cancelled" : ""}`}>
                <span className="adm-booking-date">{b.date}</span>
                <span className="adm-booking-class">
                  {b.classId.startsWith("private__")
                    ? (() => {
                        const [, date, time] = b.classId.split("__");
                        const h = parseInt(time.slice(0,2), 10);
                        const m = time.slice(2);
                        const ampm = h >= 12 ? "pm" : "am";
                        return `Private — ${date} ${h % 12 || 12}:${m} ${ampm}`;
                      })()
                    : CLASSES.find(c => c.id === b.classId)?.label ?? b.classId}
                </span>
                <span className={`adm-status-badge status-${b.status}`}>{b.status}</span>
                {b.checkedIn && <span className="adm-checkedin-tag">✓ attended</span>}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}